<script lang="ts">
  import {
    createTable,
    Render,
    Subscribe,
    createRender,
  } from "svelte-headless-table";
  import { addSortBy } from "svelte-headless-table/plugins";
  import { ArrowUp, ArrowDown } from "lucide-svelte";
  import lineItems from "$src/domain/projects/items/FakeLineItems";
  import { readable } from "svelte/store";
  import * as Table from "$lib/components/ui/table";
  import { Button } from "$lib/components/ui/button";
  import LineItemActions from "./line-item-actions.svelte";
  import LineItemCellMarginAmount from "./line-item-cell-margin-amount.svelte";
  import LineItemCellMarginPercent from "./line-item-cell-margin-percent.svelte";
  import LineItemCellDiscount from "./line-item-cell-discount.svelte";
  import * as Tooltip from "$lib/components/ui/tooltip/index.js";
  import { t } from "svelte-i18n";
  import ImageMiniature from "../image-miniature.svelte";

  const table = createTable(readable(lineItems), {
    sort: addSortBy(),
  });

  const columns = table.createColumns([
    table.column({
      id: "id",
      accessor: "id",
      header: "",
      plugins: {
        sort: {
          disable: true,
        },
      },
    }),
    table.column({
      accessor: "code",
      header: $t("page.projects.detail.lineitems.code"),
    }),
    table.column({
      id: "catalogName",
      accessor: ({ model }) => model?.catalog?.catalogName ?? "",
      header: $t("page.projects.detail.lineitems.catalogName"),
    }),
    table.column({
      id: "location",
      accessor: ({ location }) => location?.text ?? "",
      header: $t("page.projects.detail.lineitems.location"),
    }),
    table.column({
      id: "description",
      accessor: ({ product }) => product.description,
      header: $t("page.projects.detail.lineitems.description"),
    }),
    table.column({
      id: "quantity",
      accessor: "quantity",
      header: $t("page.projects.detail.lineitems.quantity"),
    }),
    table.column({
      id: "unit",
      accessor: ({ product }) => product.unit.code,
      header: $t("page.projects.detail.lineitems.unit"),
    }),
    table.column({
      id: "salesPriceExVat",
      accessor: ({ prices }) => prices.salesPriceExVat,
      header: $t("page.projects.detail.lineitems.salesPriceExVat"),
      cell: ({ value }) => {
        const formatted = new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(value);
        return formatted;
      },
    }),
    table.column({
      id: "discountPercent",
      accessor: ({ prices }) => prices.discountPercent,
      header: $t("page.projects.detail.lineitems.discount"),
      cell: ({ value }) => {
        const formatted =
          new Intl.NumberFormat("fr-FR", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value) + " %";
        return formatted;
      },
    }),
    table.column({
      id: "totalSalesPriceExVat",
      accessor: ({ prices }) => prices.totalSalesPriceExVat,
      header: $t("page.projects.detail.lineitems.totalSalesPriceExVat"),
      cell: ({ value }) => {
        const formatted = new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(value);
        return formatted;
      },
    }),
    table.column({
      id: "vat",
      accessor: ({ prices }) => prices.vatAmount,
      header: $t("page.projects.detail.lineitems.vat"),
      cell: ({ value }) => {
        const formatted =
          new Intl.NumberFormat("fr-FR", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value) + " %";
        return formatted;
      },
    }),
    table.column({
      id: "marginAmount",
      accessor: ({ prices }) => prices.marginAmount,
      header: $t("page.projects.detail.lineitems.marginAmount"),
      cell: ({ value }) => {
        const formatted = new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(value);
        return formatted;
      },
    }),
    table.column({
      id: "marginPercent",
      accessor: ({ prices }) => prices.marginPercent,
      header: $t("page.projects.detail.lineitems.marginPercent"),
      cell: ({ value }) => {
        const formatted =
          new Intl.NumberFormat("fr-FR", {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(value) + " %";
        return formatted;
      },
    }),
    table.column({
      id: "actionButton",
      accessor: ({ id }) => id,
      header: "",
      cell: ({ value }) => {
        return createRender(LineItemActions, { id: value });
      },
    }),
  ]);

  export let marginVisible: boolean = false;

  const columnClasses: Record<string, string> = {
    id: "align-top",
    code: "hidden lg:table-cell",
    catalogName: "hidden",
    location: "hidden",
    description: "max-w-80",
    quantity: "hidden sm:table-cell max-w-16",
    unit: "hidden sm:table-cell max-w-16",
    salesPriceExVat: "hidden md:table-cell",
    discountPercent: "hidden sm:table-cell max-w-16",
    totalSalesPriceExVat: "",
    vat: "hidden lg:table-cell",
    marginAmount: "hidden ",
    marginPercent: "hidden ",
    actionButton: "",
    // quantity: "truncate sm:block",
    // unit: "hidden sm:block",
    // salesPriceExVat: "hidden sm:block",
    // vat: "truncate",
    // discountPercent: "truncate",
  };

  function getColumnClass(
    cell: { id: string },
    isMarginVisible: boolean
  ): string {
    var classText = columnClasses[cell.id] ?? "";

    if (
      isMarginVisible &&
      (cell.id == "marginAmount" || cell.id == "marginPercent")
    ) {
      classText += " lg:table-cell";
    }

    return classText;
  }

  function getHeaderColumnClass(cell: { id: string }): string {
    return columnClasses[cell.id] + " sticky z-30 pos-52";
  }

  const { headerRows, pageRows, tableAttrs, tableBodyAttrs } =
    table.createViewModel(columns);
</script>

<div class="  lg:mx-0">
  <!-- <div class="rounded-md border contain-content h-full -mx-8 md:mx-0"> -->
  <!-- <div class=" overflow-x-scroll"> -->
  <!-- <div class="table_container"> -->
  <Table.Root {...$tableAttrs}>
    <Table.Header class="bg-background sticky top-[110px] z-10">
      {#each $headerRows as headerRow}
        <Subscribe rowAttrs={headerRow.attrs()}>
          <Table.Row class="">
            {#each headerRow.cells as cell (cell.id)}
              <Subscribe
                attrs={cell.attrs()}
                let:attrs
                props={cell.props()}
                let:props
              >
                <Table.Head
                  {...attrs}
                  class={getColumnClass(cell, marginVisible)}
                >
                  <!-- <th class="table-header"> -->
                  {#if cell.id === "code"}
                    <div>
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "description"}
                    <Button variant="ghost" on:click={props.sort.toggle}>
                      <Render of={cell.render()} />
                      {#if props.sort.order === "asc"}
                        <ArrowDown class={"ml-2 h-4 w-4"} />
                      {:else if props.sort.order === "desc"}
                        <ArrowUp class={"ml-2 h-4 w-4"} />
                      {/if}
                    </Button>
                  {:else if cell.id === "quantity"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "unit"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "salesPriceExVat"}
                    <div class="text-right">
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild let:builder>
                          <Button
                            builders={[builder]}
                            variant="ghost"
                            on:click={props.sort.toggle}
                          >
                            <Render of={cell.render()} />
                            {#if props.sort.order === "asc"}
                              <ArrowDown class={"ml-2 h-4 w-4"} />
                            {:else if props.sort.order === "desc"}
                              <ArrowUp class={"ml-2 h-4 w-4"} />
                            {/if}
                          </Button>
                        </Tooltip.Trigger>
                        <Tooltip.Content>
                          <p>
                            {$t("page.projects.detail.lineitems.salesPriceExVatlong")}
                          </p>
                        </Tooltip.Content>
                      </Tooltip.Root>
                    </div>
                  {:else if cell.id === "totalSalesPriceExVat"}
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild let:builder>
                        <Button
                          builders={[builder]}
                          variant="ghost"
                          on:click={props.sort.toggle}
                        >
                          <Render of={cell.render()} />
                          {#if props.sort.order === "asc"}
                            <ArrowDown class={"ml-2 h-4 w-4"} />
                          {:else if props.sort.order === "desc"}
                            <ArrowUp class={"ml-2 h-4 w-4"} />
                          {/if}
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Content>
                        <p>
                          {$t("page.projects.detail.lineitems.totalSalesPriceExVatLong")}
                        </p>
                      </Tooltip.Content>
                    </Tooltip.Root>
                  {:else if cell.id === "discountPercent"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "vat"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "marginAmount"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else if cell.id === "marginPercent"}
                    <div class="text-center">
                      <Button variant="ghost" on:click={props.sort.toggle}>
                        <Render of={cell.render()} />
                        {#if props.sort.order === "asc"}
                          <ArrowDown class={"ml-2 h-4 w-4"} />
                        {:else if props.sort.order === "desc"}
                          <ArrowUp class={"ml-2 h-4 w-4"} />
                        {/if}
                      </Button>
                    </div>
                  {:else}
                    <Render of={cell.render()} />
                  {/if}
                  <!-- </th> -->
                </Table.Head>
              </Subscribe>
            {/each}
          </Table.Row>
        </Subscribe>
      {/each}
    </Table.Header>
    <Table.Body {...$tableBodyAttrs}>
      {#each $pageRows as row (row.id)}
        <Subscribe rowAttrs={row.attrs()} let:rowAttrs>
          <Table.Row {...rowAttrs}>
            {#each row.cells as cell (cell.id)}
              <Subscribe attrs={cell.attrs()} let:attrs>
                <Table.Cell
                  {...attrs}
                  class={getColumnClass(cell, marginVisible)}
                >
                  <!-- column 1 -->
                  {#if cell.id === "id"}
                    <div class="">
                      <!-- product image -->
                      <div class="flex-shrink">
                        <ImageMiniature
                          className="w-16 h-16"
                          src="https://www.fisherpaykel.com/dw/image/v2/BCJJ_PRD/on/demandware.static/-/Sites-fpa-master-catalog/default/dwed1c0ac8/pdp/fp-02-WOSV2-30-N-10-mug-dp.png"
                        />
                      </div>
                      <div
                        class="sm:hidden pt-3 text-center font-semibold italic"
                      >
                        <!-- product quantity info -->
                        <Render of={cell.row.cellForId["quantity"].render()} />
                        <Render of={cell.row.cellForId["unit"].render()} />
                      </div>
                    </div>
                    <!-- column 2 -->
                  {:else if cell.id === "code"}
                    <div class="hidden lg:block">
                      <div class="uppercase">
                        <Render of={cell.render()} />
                      </div>
                      <div class="italic text-xs">
                        <Render
                          of={cell.row.cellForId["catalogName"].render()}
                        />
                      </div>
                    </div>
                    <!-- column 3 -->
                  {:else if cell.id === "description"}
                    <div class="h-full flex flex-col justify-between gap-2">
                      <div>
                        <span
                          class="text-secondary-foreground font-semibold mr-1"
                        >
                          <Render
                            of={cell.row.cellForId["location"].render()}
                          />
                        </span>
                        <Render of={cell.render()} />
                      </div>
                      <div
                        class="lg:hidden flex flex-row gap-2 justify-between"
                      >
                        <div class="uppercase break-all">
                          <Render of={cell.row.cellForId["code"].render()} />
                        </div>
                        <div class="italic text-xs text-right">
                          <Render
                            of={cell.row.cellForId["catalogName"].render()}
                          />
                        </div>
                      </div>
                    </div>
                    <!-- column 4 -->
                  {:else if cell.id === "quantity"}
                    <div class="text-center">
                      <Render of={cell.render()} />
                    </div>
                    <!-- column 5 -->
                  {:else if cell.id === "unit"}
                    <div class="text-center">
                      <Render of={cell.render()} />
                    </div>
                    <!-- column 6 -->
                  {:else if cell.id === "salesPriceExVat"}
                    <div class="text-right">
                      <Render of={cell.render()} />
                    </div>
                    <!-- column 7 -->
                  {:else if cell.id === "totalSalesPriceExVat"}
                    <div class="sm:h-full">
                      <div
                        class="h-10 flex flex-row justify-center items-center"
                      >
                        <Render of={cell.render()} />
                      </div>
                      <div class="h-10 sm:hidden flex flex-row justify-center">
                        <LineItemCellDiscount {cell} />
                      </div>
                      {#if marginVisible}
                        <div class="h-8 lg:hidden pt-1">
                          <LineItemCellMarginAmount {cell} />
                        </div>
                      {/if}
                    </div>
                    <!-- column 8 -->
                  {:else if cell.id === "discountPercent"}
                    <div class="h-10 flex flex-row justify-center">
                      <LineItemCellDiscount {cell} />
                    </div>
                    {#if marginVisible}
                      <div class="lg:hidden h-8 pt-1">
                        <LineItemCellMarginPercent {cell} />
                      </div>
                    {/if}
                    <!-- column 9 -->
                  {:else if cell.id === "vat"}
                    <div class="text-center">
                      <Render of={cell.render()} />
                    </div>
                    <!-- column 10 -->
                  {:else if cell.id === "marginAmount"}
                    <div class="flex justify-center">
                      <LineItemCellMarginAmount {cell} />
                    </div>
                    <!-- column 11 -->
                  {:else if cell.id === "marginPercent"}
                    <div class="flex justify-center">
                      <LineItemCellMarginPercent {cell} />
                    </div>
                  {:else if cell.id === "actionButton"}
                    <div class="sm:h-full text-center">
                      <div class="h-10 flex items-center justify-center">
                        <Render of={cell.render()} />
                      </div>
                      <div class="h-10 lg:hidden flex items-center justify-center">
                        <Render of={cell.row.cellForId["vat"].render()} />
                      </div>
                      {#if marginVisible}
                        <div class="h-8 sm:hidden pt-1">
                          <LineItemCellMarginPercent {cell} />
                        </div>
                      {/if}
                    </div>
                  {:else}
                    <Render of={cell.render()} />
                  {/if}
                </Table.Cell>
              </Subscribe>
            {/each}
          </Table.Row>
        </Subscribe>
      {/each}
    </Table.Body>
  </Table.Root>
  <!-- </div> -->
  <!-- </div> -->
</div>

<style>
  .table_container {
    position: relative;
    overflow-x: auto;
  }
  table {
    width: 100%;
  }
  th,
  td {
    padding: 8px;
    border: 1px solid #ddd;
    background-color: white;
  }
</style>
